import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { createPortal } from 'react-dom';
import Select from '../../../../../../components/Select/Select';
import Input from '../../../../../../components/Input/Input';
import { Button, Icon } from '../../../../../../components';
import FormLayout from '../../../../layout/FormLayout/FormLayout';
import { Icons } from '../../../../../../components/Icon/Icon';
import styles from '../../../../layout/FormLayout/FormLayout.module.css';

enum Forms {
    Role = 'Role',
    Base = 'Base',
}

const AssetManagerRoleForm = ({ selectedData, onClose, onSubmit }: any) => {
    const [data, setData] = useState<any>({
        roleManual: '',
        ...selectedData,
    });

    const handleSubInputChange = (prop: string) => (e: any) => {
        let value = e.target.value;

        const selectedObj = {
            ...data,
            [prop]: value,
        };

        setData(selectedObj);
    };

    return (
        <>
            {createPortal(
                <FormLayout
                    title={'Asset manager'}
                    description={'Please provide asset manager name, domicile and regulatory status.'}
                    isAbsolute={true}
                    footerMain={
                        <Button form={'subForm'} disabled={!data.roleManual}>
                            Upload
                        </Button>
                    }
                    onClose={onClose}
                >
                    <form
                        id={'subForm'}
                        onSubmit={(e) => {
                            onSubmit(e, data, {
                                roleId: uuidv4(),
                                role: data.roleManual,
                            });
                        }}
                    >
                        <Input
                            required
                            value={data.roleManual}
                            onChange={handleSubInputChange('roleManual')}
                            style={'text'}
                            max={50}
                        >
                            Short name
                        </Input>
                    </form>
                </FormLayout>,
                document && document.getElementById('formWrapper')!,
            )}
        </>
    );
};

const AssetManagerBaseForm = ({ selectedData, generalInfo, onClose, onSubmit }: any) => {
    const [showRegulatoryStatusInput, setShowRegulatoryStatusInput] = useState(!!selectedData.regulatoryStatusManual);

    const [data, setData] = useState<any>({
        name: '',
        regulatoryStatusManual: undefined,
        regulatoryStatus: undefined,
        domicile: '',
        ...selectedData,
    });

    const handleSubInputChange = (prop: string) => (e: any) => {
        let value = e.target.value;

        const selectedObj = {
            ...data,
            [prop]: value,
        };

        if (prop === 'regulatoryStatusManual' && !!value) {
            selectedObj.regulatoryStatus = undefined;
            // setShowRegulatoryStatusInput(false);
        }

        setData(selectedObj);
    };

    const handleSubSelectChange = (prop: string) => (value: any) => {
        const selectedObj = {
            ...data,
            [prop]: value,
        };

        if (prop === 'regulatoryStatus') {
            selectedObj.regulatoryStatusManual = '';
            setShowRegulatoryStatusInput(false);
        }

        setData(selectedObj);
    };

    return (
        <>
            {createPortal(
                <FormLayout
                    title={'Asset manager'}
                    description={'Please provide asset manager name, domicile and regulatory status.'}
                    isAbsolute={true}
                    footerMain={
                        <Button disabled={!data.name} form={'subForm'}>
                            Upload
                        </Button>
                    }
                    onClose={onClose}
                >
                    <form id={'subForm'} onSubmit={(e) => onSubmit(e, data)}>
                        <Input value={data.name} onChange={handleSubInputChange('name')} style={'text'} max={100}>
                            Full name
                        </Input>

                        <Select
                            value={data.domicile}
                            onChange={handleSubSelectChange('domicile')}
                            options={generalInfo.countries}
                            valueKey={'domicileId'}
                            labelKey={'domicileName'}
                            style={'secondary'}
                            placeholder={'Choose domicile'}
                        >
                            Domicile
                        </Select>

                        <hr className={`${styles.separator} ${styles.separatorMargin}`} />

                        <Select
                            value={data.regulatoryStatus}
                            onChange={handleSubSelectChange('regulatoryStatus')}
                            options={generalInfo.amRegulatoryStatuses}
                            valueKey={'regulatoryStatusId'}
                            labelKey={'regulatoryStatus'}
                            filterKey={'domicileName'}
                            onCreateOption={() => {
                                setShowRegulatoryStatusInput(true);
                            }}
                            style={'secondary'}
                            placeholder={'Choose regulatory status'}
                        >
                            Regulatory status
                        </Select>

                        {showRegulatoryStatusInput && (
                            <Input
                                onChange={handleSubInputChange('regulatoryStatusManual')}
                                style={'text'}
                                max={100}
                                value={data.regulatoryStatusManual}
                            />
                        )}
                    </form>
                </FormLayout>,
                document && document.getElementById('formWrapper')!,
            )}
        </>
    );
};

const AssetManagerSingle = ({ selectedData, generalInfo, setGeneralInfo, onUpdate, handleRemoveItem }: any) => {
    const [activeForm, setActiveForm] = useState<Forms | undefined | null>();

    const [data, setData] = useState<any>({
        domicile: undefined,
        roleManual: '',
        role: undefined,
        regulatoryStatusManual: '',
        ...selectedData,
    });

    const handleSelectChange = (prop: string) => (value: any) => {
        const selectedObj = {
            ...data,
            [prop]: value,
        };

        setData(selectedObj);
        onUpdate(selectedObj);
    };

    const handleShowCreateForm = (form: Forms) => () => {
        setActiveForm(form);
    };

    const submitActiveForm = (e: any, selectedData: any, selectedObj?: any) => {
        e.preventDefault();
        e.stopPropagation();

        const tmp = {
            ...data,
            ...selectedData,
        };

        if (selectedObj) {
            tmp.role = selectedObj;

            setGeneralInfo({
                ...generalInfo,
                amRoles: [selectedObj, ...generalInfo.amRoles],
            });
        }

        setData(tmp);
        onUpdate(tmp);
        setActiveForm(null);
    };

    return (
        <>
            <div className={styles.formGridRow}>
                <Select
                    value={data.role}
                    onChange={handleSelectChange('role')}
                    options={generalInfo.amRoles}
                    valueKey={'roleId'}
                    labelKey={'role'}
                    onCreateOption={handleShowCreateForm(Forms.Role)}
                    placeholder={'Choose asset manager role'}
                    afterLabel={
                        <button
                            style={{
                                minWidth: 32,
                            }}
                            type={'button'}
                            onClick={handleRemoveItem}
                        >
                            <Icon icon={Icons.closeRounded} />
                        </button>
                    }
                >
                    Asset manager role
                </Select>

                <Select disabled={true} placeholder={'Choose counterparty'}>
                    Asset manager agreement with
                </Select>
            </div>

            <div className={styles.formGridRow}>
                <Input
                    type={'button'}
                    value={data.name}
                    placeholder={'Asset manager name'}
                    onClick={handleShowCreateForm(Forms.Base)}
                >
                    Asset manager name
                </Input>

                <Input
                    type={'button'}
                    value={data.regulatoryStatusManual || data.regulatoryStatus?.label}
                    placeholder={'Regulatory status'}
                    onClick={handleShowCreateForm(Forms.Base)}
                >
                    Regulatory status
                </Input>
            </div>

            {activeForm === Forms.Role && (
                <AssetManagerRoleForm onSubmit={submitActiveForm} onClose={() => setActiveForm(null)} />
            )}

            {activeForm === Forms.Base && (
                <AssetManagerBaseForm
                    selectedData={data}
                    generalInfo={generalInfo}
                    onSubmit={submitActiveForm}
                    onClose={() => setActiveForm(null)}
                />
            )}
        </>
    );
};

export default AssetManagerSingle;
